/**
 * Bottom tab bar for mobile web — tables-first, wallet sheet, Grudge ID path.
 */
import { useLocation } from "wouter";
import { Coins, LayoutGrid, Shield, Spade, User, Wallet } from "lucide-react";
import { useWalletState } from "@/contexts/wallet-context";
import { formatPlayGbux } from "@shared/gbux-math";
import {
  isInPhantomInAppBrowser,
  isMobileBrowser,
  openDappInPhantomBrowser,
  phantomDappUrl,
} from "@/lib/mobile-web3";

const TABS = [
  { path: "/lobby", label: "Tables", icon: LayoutGrid },
  { path: "/gbuxgames", label: "Games", icon: Coins },
  { path: "/blackjack", label: "BJ", icon: Spade },
  { path: "/account", label: "Account", icon: User },
  { path: "__wallet__", label: "Wallet", icon: Wallet },
] as const;

export function MobileBottomNav() {
  const [loc, setLocation] = useLocation();
  const { wallet } = useWalletState();
  const isAdmin =
    wallet.publicKey === "98jzgFFkPhrw9sfr5YyttTpCBiJyid6tzxxJjXrj7xXK";
  const play = wallet.playGbux || wallet.gbuxBalance || 0;

  // Hide on live table play — action bar needs space
  if (loc.startsWith("/table")) return null;

  return (
    <nav className="mobile-bottom-nav" aria-label="Main">
      <div className="mobile-bottom-nav__row">
        {TABS.map((t) => {
          const isWallet = t.path === "__wallet__";
          const active =
            !isWallet &&
            (loc === t.path ||
              loc.startsWith(t.path + "/") ||
              (t.path === "/lobby" && loc === "/") ||
              (t.path === "/gbuxgames" &&
                (loc.startsWith("/slots") ||
                  loc.startsWith("/blackjack") ||
                  loc.startsWith("/gbux"))));
          const Icon = t.icon;
          return (
            <button
              key={t.path}
              type="button"
              className={`mobile-bottom-nav__tab${active ? " is-active" : ""}`}
              onClick={() => {
                if (isWallet) {
                  // Outside Phantom: open clean dApp in wallet browser first
                  if (
                    isMobileBrowser() &&
                    !isInPhantomInAppBrowser() &&
                    !wallet.publicKey
                  ) {
                    openDappInPhantomBrowser(phantomDappUrl("/lobby"));
                    return;
                  }
                  window.dispatchEvent(new Event("budb-open-wallet-sheet"));
                  if (!loc.startsWith("/lobby")) {
                    setLocation("/lobby");
                    window.setTimeout(
                      () =>
                        window.dispatchEvent(
                          new Event("budb-open-wallet-sheet"),
                        ),
                      120,
                    );
                  }
                  return;
                }
                setLocation(t.path);
              }}
            >
              <Icon size={22} strokeWidth={active ? 2.4 : 1.8} />
              <span>{t.label}</span>
              {isWallet && (
                <span className="mobile-bottom-nav__play">
                  {formatPlayGbux(play, { compact: true })}
                </span>
              )}
            </button>
          );
        })}
        {isAdmin && (
          <button
            type="button"
            className={`mobile-bottom-nav__tab${loc.startsWith("/admin") ? " is-active" : ""}`}
            onClick={() => setLocation("/admin")}
          >
            <Shield size={22} />
            <span>Admin</span>
          </button>
        )}
      </div>
    </nav>
  );
}
